
import { useState } from 'react';
import { CheckCircle, AlertCircle, TrendingUp, TrendingDown, ArrowRight, Share2, Download } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import type { AnalysisResult as AnalysisResultType } from '@/types/analysis';

interface AnalysisResultProps {
  transcript: string;
  feedback: AnalysisResultType['feedback'];
}

const AnalysisResult = ({ transcript, feedback }: AnalysisResultProps) => {
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleShare = async () => {
    if (!recipientEmail) {
      toast.error('Please enter an email address');
      return;
    }

    setIsSending(true);
    try {
      const { error } = await supabase.functions.invoke('send-analysis', {
        body: { recipientEmail, analysis: { transcript, feedback } }
      });

      if (error) throw error;

      toast.success('Analysis sent successfully!');
      setIsShareDialogOpen(false);
      setRecipientEmail('');
    } catch (error: any) {
      console.error('Error sending analysis:', error);
      toast.error('Failed to send analysis. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const element = document.getElementById('analysis-content');
      if (!element) {
        throw new Error('Analysis content not found');
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margins = 20;
      const contentWidth = pdfWidth - (2 * margins);
      
      const contentHeight = element.getBoundingClientRect().height;
      const scale = 2;
      
      const canvas = await html2canvas(element, {
        scale: scale,
        scrollY: -window.scrollY,
        windowHeight: contentHeight,
        height: contentHeight,
        logging: true,
        onclone: (document, element) => {
          element.style.position = 'relative';
          element.style.top = '0';
          element.style.left = '0';
          element.style.visibility = 'visible';
          element.style.padding = '40px';
          element.style.backgroundColor = 'white';
          
          const sections = element.getElementsByClassName('pdf-section');
          Array.from(sections).forEach((section: any) => {
            section.style.marginBottom = '40px';
            section.style.pageBreakInside = 'avoid';
          });
        }
      });

      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * contentWidth) / canvas.width;
      
      let heightLeft = imgHeight;
      let position = 0;

      pdf.setFontSize(24);
      pdf.setTextColor(29, 64, 175);
      pdf.text('AI Sales Analyzer', pdfWidth/2, 40, { align: 'center' });
      pdf.setFontSize(14);
      pdf.setTextColor(107, 114, 128);
      pdf.text('Analysis Report', pdfWidth/2, 50, { align: 'center' });
      pdf.addPage();

      while (heightLeft >= 0) {
        const currentHeight = Math.min(heightLeft, pdfHeight - (2 * margins));
        const canvas2 = document.createElement('canvas');
        canvas2.width = canvas.width;
        canvas2.height = (currentHeight * canvas.width) / imgWidth;
        
        const ctx = canvas2.getContext('2d');
        if (ctx) {
          ctx.drawImage(
            canvas,
            0,
            (position * canvas.width) / imgWidth,
            canvas.width,
            (currentHeight * canvas.width) / imgWidth,
            0,
            0,
            canvas.width,
            (currentHeight * canvas.width) / imgWidth
          );
        }
        
        const imgData = canvas2.toDataURL('image/png');
        if (position !== 0) {
          pdf.addPage();
        }
        
        pdf.addImage(imgData, 'PNG', margins, margins, contentWidth, currentHeight);
        heightLeft -= (pdfHeight - (2 * margins));
        position += (pdfHeight - (2 * margins));
      }

      pdf.save('sales-call-analysis.pdf');
      toast.success('Analysis downloaded successfully!');
    } catch (error) {
      console.error('Error downloading analysis:', error);
      toast.error('Failed to download analysis. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  if (!feedback || !feedback.feedback || !feedback.summary) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-600">No analysis data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn">
      <div className="flex justify-end gap-4 mb-6">
        <Button
          variant="outline"
          onClick={() => setIsShareDialogOpen(true)}
          disabled={isSending}
          className="flex items-center gap-2"
        >
          <Share2 className="w-4 h-4" />
          Share via Email
        </Button>
        <Button
          variant="outline"
          onClick={handleDownload}
          disabled={isDownloading}
          className="flex items-center gap-2"
        >
          <Download className="w-4 h-4" />
          Download PDF
        </Button>
      </div>

      <div id="analysis-content" className="space-y-12">
        <div className="p-8 bg-white rounded-lg shadow-sm border pdf-section">
          <h3 className="text-2xl font-semibold mb-8 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            Executive Summary
          </h3>
          <div className="flex items-center justify-between mb-6">
            <span className="text-lg font-medium">Overall Score</span>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold">{feedback.summary.overallScore}/10</span>
              {feedback.summary.overallScore >= 7 ? (
                <CheckCircle className="w-6 h-6 text-green-500" />
              ) : (
                <AlertCircle className="w-6 h-6 text-amber-500" />
              )}
            </div>
          </div>

          <div className="mt-6 space-y-4">
            <div className="flex items-center gap-2">
              <ArrowRight className="w-5 h-5 text-primary" />
              <h4 className="font-medium">Next Steps</h4>
            </div>
            <ul className="space-y-2">
              {feedback.summary.nextSteps.map((step, idx) => (
                <li key={idx} className="text-gray-700 flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <div>
                    <p className="font-medium">{step.action}</p>
                    <p className="text-sm text-gray-600">Implementation: {step.implementation}</p>
                    <p className="text-sm text-gray-500">Expected Outcome: {step.expectedOutcome}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="space-y-8">
          <h3 className="text-2xl font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            Detailed Analysis
          </h3>
          {feedback.feedback.map((item, index) => (
            <div
              key={index}
              className="p-8 bg-white rounded-lg shadow-sm border pdf-section"
            >
              <div className="flex items-center justify-between mb-6">
                <h4 className="text-xl font-medium">{item.category}</h4>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-medium">
                    Score: {item.score}/10
                  </span>
                  {item.score >= 7 ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-amber-500" />
                  )}
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <h5 className="font-medium text-gray-700 mb-3">What Went Well</h5>
                  <ul className="space-y-3">
                    {item.whatWentWell.map((example, idx) => (
                      <li key={idx} className="text-gray-600">
                        <p className="italic text-gray-700 mb-1">"{example.quote}"</p>
                        <p className="text-sm text-gray-600">{example.explanation}</p>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h5 className="font-medium text-gray-700 mb-3">Needs Improvement</h5>
                  <ul className="space-y-4">
                    {item.needsImprovement.map((improvement, idx) => (
                      <li key={idx} className="text-gray-600">
                        <p className="text-sm text-gray-500 mb-1">Original:</p>
                        <p className="italic text-gray-700 mb-2">"{improvement.originalQuote}"</p>
                        <p className="text-sm text-gray-500 mb-1">Better approach:</p>
                        <p className="italic text-green-700 mb-2">"{improvement.betterApproach}"</p>
                        <p className="text-sm text-gray-600">{improvement.explanation}</p>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h5 className="font-medium text-gray-700 mb-3">What Needs to be Fixed</h5>
                  <ul className="space-y-2">
                    {item.toFix.map((fix, idx) => (
                      <li key={idx} className="text-gray-600 flex items-start gap-2">
                        <span className="text-amber-500 mt-1">•</span>
                        {fix}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={isShareDialogOpen} onOpenChange={setIsShareDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share Analysis</DialogTitle>
            <DialogDescription>
              Enter the email address where you'd like to send this analysis.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email address</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter recipient's email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
              />
            </div>
            <Button
              onClick={handleShare}
              disabled={isSending}
              className="w-full"
            >
              {isSending ? 'Sending...' : 'Send Analysis'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AnalysisResult;
